import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { fadeInOut } from '../../../services/animations';
import { AlertService, DialogType, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { ReportOverviewViewModel } from '../../model/reportOverviewViewModel';
import { ReportService } from '../../api/report.service';
import { AccountService } from "../../../services/account.service";
import { ListViewViewModel } from '../../model/listViewViewModel';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { repeat } from 'rxjs/operators';

@Component({
    selector: 'app-report-list',
    templateUrl: './report-list.component.html',
    styleUrls: ['./report-list.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportListComponent implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator, { static: true })
    paginator: MatPaginator;
    @ViewChild(MatSort, { static: true })
    sort: MatSort;
    @Input() businessId: number = null;
    @Input() methodVersionId: number = null;

    displayedColumns = ['businessName', 'scoreLevelName', 'reportStatusName', 'visitDate', 'businessCategoryName', 'approvalDate', 'regionName'];
    dataSource: MatTableDataSource<ReportOverviewViewModel>;
    sourceReport: ReportOverviewViewModel;
    loadingIndicator: boolean;

    // Filtering
    @Input() currentViewId: string = "all";
    @Input() showTitle: boolean = true;
    @Input() fixedView: boolean = false;

    currentSearchText: string;

    availableViews: Observable<ListViewViewModel[]>;

    constructor(private alertService: AlertService,
        private translationService: AppTranslationService,
        private reportService: ReportService,
        private authService: AuthService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute) {

        if (this.canManageReports)
            this.displayedColumns.push('actions');

        // Assign the data to the data source for the table to render
        this.dataSource = new MatTableDataSource<ReportOverviewViewModel>();
        this.currentSearchText = "";
    }

    ngOnInit() {
        this.availableViews = this.reportService.getReportViews();

        this.availableViews.subscribe(views => {
            if (this.route.snapshot.fragment && views.find(t => t.viewId === this.route.snapshot.fragment)) {
                this.currentViewId = this.route.snapshot.fragment;
            }

            this.loadData();
        });

    }

    ngAfterViewInit(): void {
        if (!this.isEmbedded) {
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
        }
    }

    public applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue;
    }

    private refresh() {
        // Causes the filter to refresh there by updating with recently added data.
        this.applyFilter(this.dataSource.filter);
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        if (this.methodVersionId != null) {
            this.reportService.getReportsByMethodVersion(this.methodVersionId).subscribe(
                reports => this.onDataLoadSuccessful(reports),
                error => this.onDataLoadFailed(error)
            );
        } else {
            this.reportService.getReports(this.currentViewId, this.businessId).subscribe(
                reports => this.onDataLoadSuccessful(reports),
                error => this.onDataLoadFailed(error)
            );
        }
    }

    private onDataLoadSuccessful(reports: ReportOverviewViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.dataSource.data = reports;
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"), this.translationService.getTranslation("FetchErrorMessage") + "\r\n" + Utilities.getHttpResponseMessage(error),
            MessageSeverity.error, error);
    }

    get canManageReports() {
        //return this.accountService.userHasPermission(Permission.manageUsersPermission);
        return true;
    }

    public viewChanged(selectedOption: MatSelect) {
        var fragment: NavigationExtras =
        {
            fragment: selectedOption.value
        };
        this.router.navigate(['/reports'], fragment);
        this.loadData();
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeReports);
    }

    get isEmbedded() : boolean {
        return this.businessId != null || this.methodVersionId != null;
    }

    gotoReportDetails(report: ReportOverviewViewModel = null): void {

        if (report == null) {
            if (this.businessId) {
                this.router.navigate(['/reports/report'], { queryParams: { businessid: this.businessId } });
            } else {
                this.router.navigate(['/reports/report']);
            }
        } else {
            if (!report.methodVersionPreview) {
                this.router.navigate(['/reports/report', report.reportId]);
            } else {

                this.router.navigate(['/reports/report', report.reportId],
                    {
                        queryParams: {
                            methodversionid: report.methodVersionId,
                            businesscategoryid: report.businessCategoryId
                        }
                    });
            }
        }
    }
}
